// Wizard-side catalog. Consumes @protoforge/catalog (engine package) so
// capability / integration / business-type IDs cannot drift from the
// schema the runner and site-api validate against.
//
// Wizard-only presentation helpers (day labels, description chips) stay here.

import {
  BUSINESS_TYPES as ENGINE_BUSINESS_TYPES,
  CAPABILITIES,
  INTEGRATIONS,
  integrationsForType,
  type CapabilityDef,
  type IntegrationDef,
} from "@protoforge/catalog";
import type { BusinessType } from "./api";

export type { CapabilityDef, IntegrationDef };
export { CAPABILITIES, INTEGRATIONS, integrationsForType };

/** Wizard card shape — maps engine `{ id, label, hint }` → `{ value, label, example }`. */
export const BUSINESS_TYPES: Array<{ value: BusinessType; label: string; example: string }> =
  ENGINE_BUSINESS_TYPES.map((b) => ({
    value: b.id,
    label: b.label,
    example: b.hint,
  }));

export const DAYS_OF_WEEK = [
  { id: "mon", label: "Monday" },
  { id: "tue", label: "Tuesday" },
  { id: "wed", label: "Wednesday" },
  { id: "thu", label: "Thursday" },
  { id: "fri", label: "Friday" },
  { id: "sat", label: "Saturday" },
  { id: "sun", label: "Sunday" },
];

export type Hours = Record<string, { open: string; close: string; closed: boolean }>;

export function defaultHours(): Hours {
  const out: Hours = {};
  for (const d of DAYS_OF_WEEK) {
    out[d.id] = { open: "09:00", close: "17:00", closed: d.id === "sun" };
  }
  return out;
}

// Per-type starter descriptions. The wizard surfaces these as click-to-
// use chips so the customer has something to react to instead of a
// blank textarea. Each entry is a complete example sentence pitched
// at a *different* business archetype within the type — the customer
// picks the closest one and edits it. Never spliced with the
// customer's actual business name (the templates are domain-specific
// and would read absurd if "Joe's Bike Shop is a plant shop").

export const DESCRIPTION_SUGGESTIONS: Record<BusinessType, string[]> = {
  restaurant: [
    "A family-owned neighborhood spot serving wood-fired pizzas and seasonal small plates. Wine list leans natural, kid-friendly until 8pm.",
    "A slow-brewed coffee shop with scratch pastries and a rotating breakfast menu. Open early, named after the dog out front.",
    "A modern American restaurant with a focus on local farms and house-made charcuterie. Reservations recommended on weekends.",
  ],
  retail: [
    "A curated boutique stocking independent brands you won't find at the mall — mostly clothing, a little jewelry, all small-batch.",
    "A bike shop and full-service repair where we sell what we ride. Come by for tube swaps, fits, or the Saturday morning group ride.",
    "A plant shop and design studio specializing in low-light houseplants and one-on-one consultations for new plant owners.",
  ],
  service: [
    "Licensed and insured local plumber offering same-day service for leaks, drains, and water heaters. Family-run since 2014.",
    "Residential and light-commercial electricians doing clean panels, smart-home wiring, and EV charger installs. Free estimates.",
    "House cleaning service that respects your time and your stuff. Recurring schedules, vetted teams, eco-friendly products by default.",
  ],
  professional: [
    "Boutique law practice focused on small-business formation, contracts, and IP. Flat-fee packages for most engagements.",
    "Tax and bookkeeping for freelancers and solo founders. Quarterly check-ins, no jargon, clear pricing.",
    "Brand strategy consultancy helping early-stage teams find their voice and ship a website they're not embarrassed by.",
  ],
  health: [
    "Independent salon and spa offering hair, nails, and skincare in a calm, low-fragrance environment. Book online, same-day appointments common.",
    "Family dentistry and orthodontics with new-patient exams, clear aligners, and a waiting room that doesn't smell like a clinic.",
    "Yoga studio with daily classes for every level. Heated and unheated rooms, drop-ins welcome, monthly intro packages for new students.",
  ],
  creative: [
    "Wedding and lifestyle photographer rooted in natural light and quiet moments. Full-day coverage, hand-edited galleries delivered in two weeks.",
    "Independent design studio specializing in identity systems and websites for restaurants, hotels, and small consumer brands.",
    "Portrait and editorial photographer based in Asheville. Studio and on-location, comfortable with everything from headshots to album art.",
  ],
  other: [
    "A small business serving its neighborhood with care. Tell us a bit more about what you do — two or three sentences is plenty.",
    "An independent operation doing one thing well. Keep it conversational here; the agent rewrites it for the site.",
  ],
};
